import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  Tooltip,
  Modal,
  Backdrop,
  TextField,
  Fade,
} from "@mui/material";
import api from "../../helper/baseApi";
import { useApiClient } from "../../context/ApiClientContext";
import { useNavigate } from "react-router-dom";
import ErrorAlert from "../../helper/errorAlert";
import toast from "react-hot-toast";

const CampaignInfo = ({ data, id }) => {
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const {
    apiUrls: { mobileApiUrl },
  } = useApiClient();
  const navigate = useNavigate();
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [remark, setRemark] = useState("");
  const [openModal, setOpenModal] = useState(false);

  if (!data) return null;

  const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (isNaN(d)) return "-";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const onSuspend = async () => {
    try {
      setOpenBackdrop(true);
      setOpenModal(false);
      let response = await api.post(
        `${mobileApiUrl}reward/reward-cancel-portal`,
        {
          rewardId: id,
          remark: remark.trim(),
        }
      );

      if (response.status === 200) {
        toast.success("Campaign suspended successfully");
        navigate("/campaign");
      }
    } catch (error) {
      ErrorAlert(error, navigate);
    } finally {
      setOpenBackdrop(false);
    }
  };

  const handleOpenRemarkModal = () => setShowRemarkModal(true);
  const handleCloseRemarkModal = () => {
    setShowRemarkModal(false);
    setRemark("");
  };

  // 🧩 Reusable info row component for aligned layout
  const InfoRow = ({ label, value, tooltip }) => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "160px 10px 1fr", // label | colon | value
        alignItems: "start",
      }}
    >
      <Typography variant="body" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="body">:</Typography>
      {tooltip ? (
        <Tooltip title={value || "-"}  arrow>
          <Typography
            variant="body"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "normal",
            }}
          >
            {value || "-"}
          </Typography>
        </Tooltip>
      ) : (
        <Typography variant="body">{value || "-"}</Typography>
      )}
    </Box>
  );

  return (
    <>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography
          variant="subtitle"
          sx={{ fontWeight: 700, textTransform: "uppercase" }}
        >
          Campaign Info
        </Typography>

        {(data.status === "InProgress" || data.status === "Accomplish") && (
          <Button
            variant="contained"
            size="medium"
            onClick={handleOpenRemarkModal}
            sx={{
              backgroundColor: "secondary.main",
              fontWeight: "600",
              "&:hover": { backgroundColor: "secondary.dark" },
            }}
          >
            Suspend
          </Button>
        )}
      </Box>

      {/* Info Grid */}
      <Grid
        container
        rowSpacing={1}
        columnSpacing={{ xs: 1, sm: 2, md: 3 }}
        sx={{ mb: 4, ml: 2 }}
      >
        <Grid size={12}>
          <InfoRow label="Campaign Title" value={data.name} />
        </Grid>

        <Grid size={12}>
          <InfoRow label="Description" value={data.description} tooltip />
        </Grid>

        <Grid size={6}>
          <InfoRow label="Entitlement Point" value={data.entitlementPoint} />
        </Grid>

        <Grid size={6}>
          <InfoRow label="Status" value={data.status} />
        </Grid>

        <Grid size={6}>
          <InfoRow label="Start Date" value={data.startDate} />
        </Grid>

        <Grid size={6}>
          <InfoRow label="Created By" value={data.createdName} />
        </Grid>

        <Grid size={6}>
          <InfoRow label="End Date" value={data.endDate} />
        </Grid>

        <Grid size={6}>
          <InfoRow label="Created Date" value={formatDate(data.createdDate)} />
        </Grid>

        <Grid size={6}>
          <InfoRow label="Quantity" value={data.qty} />
        </Grid>
      </Grid>

      {/* Remark Modal */}
      <Modal
        open={showRemarkModal}
        onClose={handleCloseRemarkModal}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 500 } }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Fade in={showRemarkModal} timeout={500}>
          <Box
            sx={{
              bgcolor: "background.paper",
              boxShadow: 24,
              px: 3,
              pb: 3,
              py: 2,
              borderRadius: 2,
              overflowY: "auto",
              position: "relative",
              width: { xs: "90%", sm: "70%", md: "40%" },
              maxHeight: "90vh",
            }}
          >
            <Typography
              variant="subtitle"
              sx={{
                mt: 3,
                mb: 2,
                fontWeight: 600,
                textTransform: "uppercase",
              }}
            >
              Suspend Campaign
            </Typography>

            <TextField
              fullWidth
              label="Remark"
              multiline
              minRows={3}
              variant="outlined"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              sx={{ mb: 3, mt: 2 }}
            />

            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleCloseRemarkModal}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={onSuspend}
                disabled={!remark.trim()}
              >
                Confirm
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

export default CampaignInfo;
